import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@astryxdesign/core/Button";
import { Text } from "@astryxdesign/core/Text";
import { VStack } from "@astryxdesign/core/VStack";
import { publicApi } from "@/lib/api/public";
import type { Service, Slot, Studio } from "@/lib/api/schemas";
import { addDays, formatDay, formatLongDay, todayIn } from "@/lib/format";
import { ErrorState, LoadingRows, Empty } from "@/components/states";
import { useT } from "@/lib/i18n";

export const slotsQueryKey = (slug: string, serviceId: string, from: string) => ["slots", slug, serviceId, from] as const;

/** Day strip + time grid. Availability comes from the server per 7-day chunk. */
export function TimeStep({
  studio,
  service,
  date,
  onDate,
  onPick,
}: {
  studio: Studio;
  service: Service;
  date: string | null;
  onDate: (d: string) => void;
  onPick: (slot: Slot) => void;
}) {
  const t = useT();
  const today = todayIn(studio.timezone);
  const days = useMemo(() => Array.from({ length: studio.rules.horizon_days }, (_, i) => addDays(today, i)), [today, studio.rules.horizon_days]);
  const selected = date ?? today;
  const chunkIndex = Math.max(0, Math.floor(days.indexOf(selected) / 7));
  const chunkFrom = addDays(today, chunkIndex * 7);

  const slots = useQuery({
    queryKey: slotsQueryKey(studio.slug, service.id, chunkFrom),
    queryFn: () => publicApi.slots(studio.slug, service.id, chunkFrom, 7),
    staleTime: 30_000,
  });

  const byDay = useMemo(() => {
    const m = new Map<string, Slot[]>();
    for (const s of slots.data ?? []) m.set(s.local_date, [...(m.get(s.local_date) ?? []), s]);
    return m;
  }, [slots.data]);
  const daySlots = byDay.get(selected) ?? [];
  const chunkDays = days.slice(chunkIndex * 7, chunkIndex * 7 + 7);

  return (
    <VStack gap={4}>
      <nav aria-label={t.time.dateChoice}>
        <ul className="date-strip list-none">
          {days.map((d) => {
            const f = formatDay(d);
            const known = chunkDays.includes(d) && !slots.isPending;
            const empty = known && !byDay.has(d);
            return (
              <li key={d}>
                <Button
                  label={`${f.weekday} ${f.day}`}
                  variant={d === selected ? "primary" : "secondary"}
                  size="md"
                  isDisabled={empty}
                  onClick={() => onDate(d)}
                  aria-pressed={d === selected}
                  aria-label={`${formatLongDay(d)}${empty ? t.time.noFreeSuffix : ""}`}
                />
              </li>
            );
          })}
        </ul>
      </nav>

      <VStack gap={2} aria-live="polite">
        <Text type="label" color="secondary">
          {formatLongDay(selected)}
        </Text>
        {slots.isPending ? (
          <LoadingRows rows={2} height={44} label={t.time.loadingSlots} />
        ) : slots.isError ? (
          <ErrorState error={slots.error} onRetry={() => void slots.refetch()} />
        ) : daySlots.length === 0 ? (
          <Empty title={t.time.noFreeTitle} description={t.time.pickOtherDay} />
        ) : (
          <ul className="slot-grid list-none" aria-label={t.time.freeTime}>
            {daySlots.map((s) => (
              <li key={s.starts_at}>
                <Button label={s.local_time} variant="secondary" width="100%" onClick={() => onPick(s)} />
              </li>
            ))}
          </ul>
        )}
        {service.multi_day ? (
          <Text type="supporting">{t.time.multiDay(Math.round(service.duration_minutes / 1440))}</Text>
        ) : null}
        <Text type="supporting">{t.time.tzNote(studio.timezone)}</Text>
      </VStack>
    </VStack>
  );
}
